import {
  VestingGraphData,
  VestingGraphPointStatus,
} from '../../../../../hooks/vesting-task/use-vesting-graph';
import { formatVestingCalendarDate } from './vesting-graph.format';
import {
  resolveMilestoneEventLayoutTimestamp,
  resolveMilestoneSegmentBounds,
} from './vesting-graph.geometry';
import { insertScheduleEventColumns } from './vesting-graph.layout';
import { DateFormatter, VestingGraphSeriesPoint } from './vesting-graph.types';

/** Expired payments keep the unvested wash; only the expiry marker stays a dedicated column. */
const isProjectedStatus = (status: VestingGraphPointStatus) =>
  status === VestingGraphPointStatus.UNVESTED || status === VestingGraphPointStatus.EXPIRED;

const emptySeriesPoint = ({
  calendarTimestamp,
  cumulativePercent,
  cumulativeShares,
  dateLabel,
  status,
  timestamp,
  vestingStepShares = 0,
}: {
  /** Real milestone / event date when `timestamp` is a layout segment edge. */
  calendarTimestamp?: number;
  cumulativePercent: number;
  cumulativeShares: number;
  dateLabel: string;
  status: VestingGraphPointStatus;
  timestamp: number;
  vestingStepShares?: number;
}): VestingGraphSeriesPoint => ({
  calendarTimestamp: calendarTimestamp ?? timestamp,
  cumulativePercent,
  cumulativeShares,
  dateLabel,
  expiredPercent: null,
  monthsToComplete: 0,
  originalSchedulePercent: null,
  periodShares: 0,
  projectedPercent: null,
  slotHalfWidth: 0,
  status,
  timestamp,
  vestedPercent: null,
  vestingStepShares,
});

export const buildMilestoneVestingGraphChartData = ({
  format,
  vestingGraph,
}: {
  format: DateFormatter;
  vestingGraph: VestingGraphData;
}) => {
  const scheduleDomainMin = new Date(vestingGraph.startDate).getTime();
  const lastPointTime = vestingGraph.points.at(-1)
    ? new Date(vestingGraph.points.at(-1)!.date).getTime()
    : scheduleDomainMin;
  const fullVestTime = vestingGraph.fullVestingDate
    ? new Date(vestingGraph.fullVestingDate).getTime()
    : lastPointTime;
  const scheduleEventTimes = [vestingGraph.accelerationDate, vestingGraph.expiryDate]
    .filter((date): date is string => Boolean(date))
    .map((date) => new Date(date).getTime());
  // Cover milestones, full vesting, and schedule events before layout so equal-column
  // x and event x share one domain.
  const domainMin = Math.min(scheduleDomainMin, ...scheduleEventTimes, lastPointTime);
  const domainMax = Math.max(scheduleDomainMin, lastPointTime, fullVestTime, ...scheduleEventTimes);
  const domainSpan = Math.max(domainMax - domainMin, 1);
  const segmentBounds = resolveMilestoneSegmentBounds({
    domainMax,
    domainMin,
    points: vestingGraph.points,
  });
  const milestoneDates = vestingGraph.points.map((point) => new Date(point.date).getTime());

  const chartData: VestingGraphSeriesPoint[] = [];
  let previousPercent = 0;
  let previousShares = 0;
  let previousVestedPercent = 0;
  let previousVestedShares = 0;

  vestingGraph.points.forEach((point, index) => {
    const bounds = segmentBounds[index]!;
    const segmentSpan = Math.max(bounds.endTimestamp - bounds.startTimestamp, 0);
    const gapMs = Math.min(domainSpan * 0.012, segmentSpan * 0.1);
    const segmentStart = index === 0 ? bounds.startTimestamp : bounds.startTimestamp + gapMs / 2;
    const segmentEnd = Math.max(segmentStart, bounds.endTimestamp - gapMs / 2);
    const nullTimestamp = Math.min(
      segmentEnd + Math.max(gapMs / 2, 1),
      index < vestingGraph.points.length - 1
        ? segmentBounds[index + 1]!.startTimestamp + gapMs / 2 - 1
        : segmentEnd + Math.max(gapMs / 2, 1),
    );
    const isProjected = isProjectedStatus(point.status);
    // Layout x is the segment edge; calendar stays the real milestone date for event
    // columns, markers, and inactive-accel checks.
    const milestoneTime = new Date(point.date).getTime();
    // Vested columns start from the last actually vested holding. Unvested neighbours
    // still project the sequential plan height, which can sit above a later out-of-order
    // approval — connecting through that peak would draw the vested ramp downhill.
    const columnStartPercent = isProjected ? previousPercent : previousVestedPercent;
    const columnStartShares = isProjected ? previousShares : previousVestedShares;
    const startPercent = Math.min(columnStartPercent, point.cumulativePercent);
    const startShares = Math.min(columnStartShares, point.cumulativeShares);

    const statusKey = isProjected ? 'projectedPercent' : 'vestedPercent';
    const columnBounds = {
      columnEndTimestamp: bounds.endTimestamp,
      columnStartTimestamp: bounds.startTimestamp,
    };

    chartData.push({
      ...emptySeriesPoint({
        calendarTimestamp: milestoneTime,
        cumulativePercent: startPercent,
        cumulativeShares: startShares,
        dateLabel: formatVestingCalendarDate(format, point.date),
        status: point.status,
        timestamp: segmentStart,
        vestingStepShares: point.periodShares,
      }),
      ...columnBounds,
      [statusKey]: startPercent,
      milestoneName: point.milestoneName,
      periodShares: 0,
    });
    chartData.push({
      ...emptySeriesPoint({
        calendarTimestamp: milestoneTime,
        cumulativePercent: point.cumulativePercent,
        cumulativeShares: point.cumulativeShares,
        dateLabel: formatVestingCalendarDate(format, point.date),
        status: point.status,
        timestamp: segmentEnd,
        vestingStepShares: point.periodShares,
      }),
      ...columnBounds,
      [statusKey]: point.cumulativePercent,
      milestoneName: point.milestoneName,
      monthsToComplete: point.monthsToComplete,
      periodShares: point.periodShares,
    });
    // Null break with a distinct x — opens the white gutter before the next window.
    chartData.push({
      ...emptySeriesPoint({
        calendarTimestamp: milestoneTime,
        cumulativePercent: point.cumulativePercent,
        cumulativeShares: point.cumulativeShares,
        dateLabel: formatVestingCalendarDate(format, point.date),
        status: point.status,
        timestamp: nullTimestamp,
        vestingStepShares: point.periodShares,
      }),
      ...columnBounds,
      milestoneName: point.milestoneName,
    });

    previousPercent = point.cumulativePercent;
    previousShares = point.cumulativeShares;
    if (!isProjected) {
      previousVestedPercent = point.cumulativePercent;
      previousVestedShares = point.cumulativeShares;
    }
  });

  // Expiry / accel: insert by calendar order, then put layout x into segment space so
  // equal-column charts stay monotonic.
  const chartDataWithEvents = insertScheduleEventColumns({
    chartData,
    format,
    vestingGraph,
  });
  const eventHalfWidth = Math.max(domainSpan * 0.02, 1) / 2;
  const chartDataWithWidths = chartDataWithEvents.map((point) =>
    point.isEventColumn && point.slotHalfWidth === 0
      ? { ...point, slotHalfWidth: eventHalfWidth }
      : point,
  );
  // Inserted events (no milestoneName) still carry calendar `timestamp` from
  // insertScheduleEventColumns — remap into the open layout gutter between neighbours.
  const chartDataAligned = [...chartDataWithWidths];
  for (let index = 0; index < chartDataAligned.length; index += 1) {
    const point = chartDataAligned[index]!;
    if (!point.isEventColumn || point.milestoneName) continue;

    const previousTimestamp = chartDataAligned[index - 1]?.timestamp ?? domainMin;
    let nextTimestamp = domainMax;
    for (let lookAhead = index + 1; lookAhead < chartDataAligned.length; lookAhead += 1) {
      const candidate = chartDataAligned[lookAhead]!;
      if (candidate.isEventColumn && !candidate.milestoneName) continue;
      nextTimestamp = candidate.timestamp;
      break;
    }

    const preferredTimestamp = resolveMilestoneEventLayoutTimestamp({
      calendarTimestamp: point.calendarTimestamp,
      domainMax,
      domainMin,
      milestoneDates,
      segmentBounds,
    });
    const upperBound = nextTimestamp > previousTimestamp ? nextTimestamp : previousTimestamp;
    chartDataAligned[index] = {
      ...point,
      timestamp: Math.min(Math.max(preferredTimestamp, previousTimestamp), upperBound),
    };
  }

  return {
    chartData: chartDataAligned,
    domainMax,
    domainMin,
    milestoneSegmentBounds: segmentBounds,
  };
};
